import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import * as moment from 'moment'
import { IndividualConfig, ToastrService } from 'ngx-toastr'
import { GlobalConstants } from 'src/app/constants/global-constants'
import { CodeLookup } from '../../models/api.model'
import { format, parse } from 'date-fns'

@Injectable({
  providedIn: 'root'
})
export class CommonService {
  constructor(private router: Router, private toastr: ToastrService) {}

  createToasterConfig(): Partial<IndividualConfig> {
    return {
      enableHtml: true,
      progressBar: true,
      progressAnimation: 'decreasing',
      tapToDismiss: false,
      closeButton: true
    }
  }

  routeTo = (routingURL: string) => {
    this.router.navigateByUrl(routingURL)
  }

  routeWithQueryParams = (routingURL: any, queryParams: Array<any>) => {
    this.router.navigate([routingURL, ...queryParams])
  }

  route = (...url: string[]) => {
    this.router.navigateByUrl(this.createRoutingURL(url))
  }

  createRoutingURL = (url: string[]): string => {
    return url.reduce((acc, segment) => `${acc}/${segment}`)
  }

  isDate = (date: any): boolean => {
    const parsedDate = Date.parse(date)
    return (isNaN(date) && !isNaN(parsedDate)) || Object.prototype.toString.call(date) === '[object Date]'
  }

  isNumber = (date: any): boolean => {
    return Object.prototype.toString.call(date) === '[object Number]'
  }

  isString = (date: any): boolean => {
    return Object.prototype.toString.call(date) === '[object String]'
  }

  isOfSameType = (input1: any, input2: any): boolean => {
    return Object.prototype.toString.call(input1) === Object.prototype.toString.call(input2)
  }

  createShowMessage =
    (type: string) =>
    (msg: string, title: string = 'SOP', enableHtml: boolean = false) => {
      const toasterConfig = this.createToasterConfig()
      this.toastr[type](msg, title, toasterConfig)
    }

  showSuccess = this.createShowMessage('success')

  showError = this.createShowMessage('error')

  showWarning = this.createShowMessage('warning')

  showInfo = this.createShowMessage('info')

  findObj = (list: any[], keyToCheckInObj: string, valueToMatch: string) => {
    return list.find(obj => obj[keyToCheckInObj] === valueToMatch)
  }

  extractApiErrorMessage(err: unknown): string | undefined {
    if (!err) {
      return undefined
    }

    if (typeof err === 'string') {
      try {
        const parsed = JSON.parse(err)
        if (parsed?.status === 429) {
          return parsed?.error
        }
        return parsed?.error?.message
      } catch {
        return undefined
      }
    }

    if ((err as any)?.error?.message) {
      return (err as any).error.message
    }

    return undefined
  }

  routeWithPathParams(routingURL: string, pathParams: string[] = []) {
    this.router.navigate(['/', routingURL, ...pathParams])
  }
}

export const formatDatetoYYYYMMDD = (dateString: string) => {
  if (!dateString) {
    return ''
  }
  const [datePart] = dateString.split('T')
  const date = new Date(datePart)
  return date.toISOString().slice(0, 10)
}

export const addDaysToDate = (days: number) => {
  const currentDate = addDaysSkippingWeekends(days, new Date())

  const year = currentDate.getFullYear()
  const month = (currentDate.getMonth() + 1).toString().padStart(2, '0')
  const day = currentDate.getDate().toString().padStart(2, '0')

  return `${year}-${month}-${day}`
}

export const formatBooleanToYesNo = (value: boolean) => {
  if (value) {
    return 'YES'
  } else {
    return 'NO'
  }
}

export const formatYesNoToBoolean = (value: number) => {
  if (value == 0) {
    return true
  } else {
    return false
  }
}

export const selectCategoryType = (value: any) => {
  if (value == 0) {
    return 'NOTIFICATION'
  } else if (value == 1) {
    return 'CIRCULAR'
  } else {
    return ''
  }
}

export const formatDate = (dateString: string, format: string) => {
  return moment(dateString).format(format)
}

export const formatDateWith3Params = (date: Date | string, inputFormat: string, outputFormat: string): string => {
  if (!date) return ''
  const parsedDate = typeof date === 'string' ? parse(date, inputFormat, new Date()) : date
  return format(parsedDate, outputFormat)
}

export const formatDateToDDMMYYYY = (dateString: string) => {
  if (!dateString) {
    return ''
  }
  const options = { day: '2-digit', month: '2-digit', year: 'numeric' } as Intl.DateTimeFormatOptions
  const [datePart] = dateString.split('T')
  const date = new Date(datePart)
  return date.toLocaleDateString('en-GB', options).replace(/\//g, '/')
}

export const addDaysToUIFormattedDate = (days: number) => {
  const currentDate = addDaysSkippingWeekends(days, new Date())

  const year = currentDate.getFullYear()
  const month = (currentDate.getMonth() + 1).toString().padStart(2, '0')
  const day = currentDate.getDate().toString().padStart(2, '0')

  return `${day}/${month}/${year}`
}

export const addDaysToEditableDatePickerFormat = (days: number): string => {
  const currentDate = addDaysSkippingWeekends(days, new Date())

  const year = currentDate.getFullYear()
  const month = (currentDate.getMonth() + 1).toString().padStart(2, '0')
  const day = currentDate.getDate().toString().padStart(2, '0')

  return `${year}-${month}-${day}`
}

export const removeEmptyKeys = (obj: any) => {
  const newObj = { ...obj }

  for (const key in newObj) {
    if (newObj[key] === null || newObj[key] === '') {
      delete newObj[key]
    }
  }

  return newObj
}

export const convertToISO = (input: string): string | null => {
  let dateMoment: moment.Moment

  if (typeof input === 'string') {
    const formatsToTry = ['DD/MM/YYYY', 'DD-MM-YYYY', 'MM-DD-YYYY', 'MM/DD/YYYY', 'M/D/YY', 'M-D-YY']
    for (const format of formatsToTry) {
      dateMoment = moment(input, format, true)
      if (dateMoment.isValid()) {
        break
      }
    }
  }

  if (dateMoment.isValid()) {
    return dateMoment.endOf('day').toISOString()
  } else {
    return null
  }
}

export const convertISOToDate = (dateString: string) => {
  if (!dateString) {
    return null
  }
  const dateObject = moment(dateString)
  if (!dateObject.isValid()) {
    return null
  }
  return moment(dateObject.toDate()).format('DD-MM-YYYY')
}

export const selectEmployeeStatus = (value: any) => {
  if (value == 0) {
    return GlobalConstants.EMPLOYEE_STATUS.ACTIVE
  } else if (value == 1) {
    return GlobalConstants.EMPLOYEE_STATUS.INACTIVE
  } else if (value == 2) {
    return GlobalConstants.EMPLOYEE_STATUS.TERMINATED
  } else {
    return ''
  }
}

export const removeNullUndefinedEmpty = (obj: any) => {
  if (Array.isArray(obj)) {
    const filteredArray = obj.map(element => removeNullUndefinedEmpty(element)).filter(element => element !== null)

    return filteredArray.length > 0 ? filteredArray : null
  } else if (typeof obj === 'object' && obj !== null) {
    const result: any = {}
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = removeNullUndefinedEmpty(obj[key])
        if (value !== null && value !== undefined) {
          result[key] = value
        }
      }
    }
    return Object.keys(result).length > 0 ? result : null
  } else if (obj === '') {
    return null
  } else {
    return obj
  }
}

export const addDaysSkippingWeekends = (days: number, currentDate: Date) => {
  let responseDueDate = new Date()
  let addedDays = 0

  while (addedDays < days) {
    if (isWeekday(currentDate)) {
      addedDays++
      responseDueDate = new Date(currentDate)
    }
    currentDate.setDate(currentDate.getDate() + 1)
  }
  return responseDueDate
}

const isWeekday = (date: Date): boolean => {
  const day = date.getDay()
  return day >= 1 && day <= 5
}

export const generateOptionsMap = (categories: any[]) => {
  let optionsMap = new Map<string, string>()
  categories?.forEach(item => {
    optionsMap.set(item.key1, item.key2)
  })
  return optionsMap
}

export const getOptionsValue = (codeLookUpArr: CodeLookup[], value: string | undefined): string => {
  const option = codeLookUpArr?.find(item => item.key1 === value)
  return option ? option.key2 : ''
}

export const getMonthOptions = () => {
  const options = []
  const currentDate = moment()
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

  for (let i = 0; i < 6; i++) {
    const date = currentDate.clone().subtract(i, 'months').startOf('month')
    const month = monthNames[date.month()]
    const year = date.year()

    const formattedStartOfMonth = date.format('YYYY-MM-DD HH:mm:ss')
    const formattedEndOfMonth = date.clone().endOf('month').format('YYYY-MM-DD HH:mm:ss')

    options.push({
      key: `${formattedStartOfMonth}^${formattedEndOfMonth}`,
      value: `${month.toUpperCase()} ${String(year).slice(-2)}`
    })
  }

  return options
}

export const generateOptionsMapFromSet = set => {
  const resultMap = new Map()
  set.forEach(value => {
    resultMap.set(value, value)
  })
  return resultMap
}

export const isIntelTenant = (hostname: string = window?.location?.hostname): boolean => {
  try {
    return hostname?.toLowerCase()?.includes('intel') ?? false
  } catch {
    return false
  }
}

export const isADITenant = (hostname: string = window?.location?.hostname): boolean => {
  try {
    return hostname?.toLowerCase()?.includes('adi') ?? false
  } catch {
    return false
  }
}